/**
 * PipelineTable (MUI)
 *
 * PROMPT 단위 파이프라인 현황 테이블. 컨버터 / fail_fix / validate_fix / fix_loop 상태를 한눈에.
 * teams-tab PipelineTable.tsx 의 MUI 포팅 — 핵심 기능(조회/액션/배치)만 포함.
 * 스킵: 커스텀 마크다운 렌더러, bridge NDJSON 파싱(후속 포팅).
 */

import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";

import {
  fixLoopColor,
  fixLoopLabel,
  fixLoopTooltip,
  formatTime,
  getNextAction,
  type PromptRow,
} from "../helpers/pipeline";
import { fetchValidationReport, fetchConverterMetadata, type ValidationReport } from "../api/skills";
import type { ConverterPromptSummary, ConverterMetadataResponse } from "../api/types";
import { PromptDetailDialog } from "./PromptDetailDialog";

export type ActionType = "converter" | "fail_fix" | "validate_fix";

interface PipelineTableProps {
  rows: PromptRow[];
  loading: boolean;
  runningSkills: Set<string>;
  converterRunning: boolean;
  batchRunning: boolean;
  batchProgress: { current: number; total: number } | null;
  selected: Set<string>;
  /** validation-report fetch 에 필요. 없으면 상세 미표시. */
  tenantCode?: string;
  bsda?: string;
  onToggle: (pid: string) => void;
  onToggleAll: () => void;
  onRefresh: () => void;
  onRunConverter: (pid: string) => void;
  onRunSkill: (skill: "fail_fix" | "validate_fix", pid: string) => void;
  onBatchRun: (type: ActionType) => void;
  // Phase-2 enrichments (only effective when featureV2 is true)
  summaryByPrompt?: Map<string, ConverterPromptSummary>;
  featureV2?: boolean;
}

function statusChip(status: PromptRow["converterStatus"]) {
  switch (status) {
    case "success":
      return <Chip size="small" color="success" variant="outlined" label="성공" />;
    case "failed":
      return <Chip size="small" color="error" variant="outlined" label="실패" />;
    case "no_file":
      return <Chip size="small" color="warning" variant="outlined" label="파일없음" />;
    default:
      return <Chip size="small" variant="outlined" label="미실행" />;
  }
}

function skillChip(entry: PromptRow["failFix"]): React.ReactNode {
  if (!entry) return <Typography variant="caption" color="text.secondary">—</Typography>;
  const color =
    entry.status === "completed"
      ? "success"
      : entry.status === "running"
        ? "warning"
        : "error";
  const label =
    entry.status === "completed"
      ? "완료"
      : entry.status === "running"
        ? "실행중"
        : "실패";
  return <Chip size="small" color={color as "success" | "warning" | "error"} variant="outlined" label={label} />;
}

export function PipelineTable({
  rows,
  loading,
  runningSkills,
  converterRunning,
  batchRunning,
  batchProgress,
  selected,
  tenantCode,
  bsda,
  onToggle,
  onToggleAll,
  onRefresh,
  onRunConverter,
  onRunSkill,
  onBatchRun,
  summaryByPrompt,
  featureV2 = false,
}: PipelineTableProps) {
  const [expanded, setExpanded] = useState<string | null>(null);
  // Phase-2: 세션 상세 모달 (탭3 SessionDetailDialog 강화).
  const [detailPromptId, setDetailPromptId] = useState<string | null>(null);

  const summary = useMemo(() => {
    const cvt = { success: 0, failed: 0, no_file: 0, not_run: 0 };
    let done = 0;
    for (const r of rows) {
      cvt[r.converterStatus]++;
      if (getNextAction(r) === "done") done++;
    }
    return { cvt, done };
  }, [rows]);

  const allSelected = selected.size === rows.length && rows.length > 0;
  const someSelected = selected.size > 0 && !allSelected;

  if (loading && rows.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (rows.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          등록된 PROMPT가 없습니다.
        </Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={1.5}>
      {/* Summary */}
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Chip size="small" variant="outlined" label={`${rows.length}개 PROMPT`} />
        {summary.done > 0 && (
          <Chip size="small" color="success" variant="outlined" label={`완료 ${summary.done}`} />
        )}
        <Chip size="small" color="success" variant="outlined" label={`성공 ${summary.cvt.success}`} />
        {summary.cvt.failed > 0 && (
          <Chip size="small" color="error" variant="outlined" label={`실패 ${summary.cvt.failed}`} />
        )}
        {summary.cvt.no_file > 0 && (
          <Chip size="small" color="warning" variant="outlined" label={`파일없음 ${summary.cvt.no_file}`} />
        )}
        {summary.cvt.not_run > 0 && (
          <Chip size="small" variant="outlined" label={`미실행 ${summary.cvt.not_run}`} />
        )}
        <Box sx={{ flex: 1 }} />
        <Tooltip title="새로고침">
          <IconButton size="small" onClick={onRefresh}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Batch bar */}
      {selected.size > 0 && (
        <Alert
          severity="info"
          action={
            <Stack direction="row" spacing={0.5}>
              <Button
                size="small"
                variant="contained"
                startIcon={<PlayArrowIcon />}
                onClick={() => onBatchRun("converter")}
                disabled={batchRunning}
              >
                {batchRunning && batchProgress
                  ? `컨버터 (${batchProgress.current}/${batchProgress.total})`
                  : "컨버터"}
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<PlayArrowIcon />}
                onClick={() => onBatchRun("fail_fix")}
                disabled={batchRunning}
              >
                fail_fix
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<PlayArrowIcon />}
                onClick={() => onBatchRun("validate_fix")}
                disabled={batchRunning}
              >
                validate_fix
              </Button>
              {batchRunning && <CircularProgress size={14} sx={{ ml: 1 }} />}
            </Stack>
          }
        >
          {selected.size}개 PROMPT 선택됨
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={someSelected}
                  checked={allSelected}
                  onChange={onToggleAll}
                  size="small"
                />
              </TableCell>
              <TableCell />
              <TableCell>PROMPT_ID</TableCell>
              <Tooltip title="원본 엑셀 → 정규 스키마 변환 결과 (script.py 실행)" arrow>
                <TableCell>① 컨버터</TableCell>
              </Tooltip>
              <TableCell align="right">건수</TableCell>
              {featureV2 && (
                <Tooltip title="최근 30일 내 마지막 실행 시각" arrow>
                  <TableCell>최근 실행</TableCell>
                </Tooltip>
              )}
              {featureV2 && (
                <Tooltip title="가장 최근부터 연속된 failed/partial 건수 (no_file/success 만나면 0)" arrow>
                  <TableCell>연속 실패</TableCell>
                </Tooltip>
              )}
              <Tooltip title="컨버터 실패 시 에이전트가 script.py 를 수정하는 단계" arrow>
                <TableCell>② fail_fix</TableCell>
              </Tooltip>
              <Tooltip title="수정된 결과를 독립 검증 (데이터 건수/범위/컬럼)" arrow>
                <TableCell>③ validate_fix</TableCell>
              </Tooltip>
              <Tooltip title="fix_loop 전체 상태 · 자동통과/수정후통과/진행중/에스컬레이션" arrow>
                <TableCell>④ 최종</TableCell>
              </Tooltip>
              <TableCell align="right" sx={{ width: 180 }}>액션</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const action = getNextAction(row);
              const isExpanded = expanded === row.promptId;
              const failFixRunning = runningSkills.has(`fail_fix:${row.promptId}`);
              const validateFixRunning = runningSkills.has(`validate_fix:${row.promptId}`);
              return (
                <PipelineRow
                  key={row.promptId}
                  row={row}
                  action={action}
                  isExpanded={isExpanded}
                  onExpand={() => setExpanded(isExpanded ? null : row.promptId)}
                  checked={selected.has(row.promptId)}
                  onToggle={() => onToggle(row.promptId)}
                  onRunConverter={() => onRunConverter(row.promptId)}
                  onRunFailFix={() => onRunSkill("fail_fix", row.promptId)}
                  onRunValidateFix={() =>
                    onRunSkill("validate_fix", row.promptId)
                  }
                  onShowDetail={
                    featureV2 && tenantCode && bsda
                      ? () => setDetailPromptId(row.promptId)
                      : undefined
                  }
                  converterRunning={converterRunning}
                  failFixRunning={failFixRunning}
                  validateFixRunning={validateFixRunning}
                  tenantCode={tenantCode}
                  bsda={bsda}
                  summary={summaryByPrompt?.get(row.promptId)}
                  featureV2={featureV2}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {featureV2 && tenantCode && bsda && (
        <PromptDetailDialog
          open={detailPromptId !== null}
          onClose={() => setDetailPromptId(null)}
          tenantId={tenantCode}
          promptId={detailPromptId ?? ""}
          bsda={bsda}
        />
      )}
    </Stack>
  );
}

interface PipelineRowProps {
  row: PromptRow;
  action: ReturnType<typeof getNextAction>;
  isExpanded: boolean;
  onExpand: () => void;
  checked: boolean;
  onToggle: () => void;
  onRunConverter: () => void;
  onRunFailFix: () => void;
  onRunValidateFix: () => void;
  /** V2 only — 세션 상세 모달 오픈. undefined 면 버튼 미표시 (legacy). */
  onShowDetail?: () => void;
  converterRunning: boolean;
  failFixRunning: boolean;
  validateFixRunning: boolean;
  tenantCode?: string;
  bsda?: string;
  summary?: ConverterPromptSummary;
  featureV2?: boolean;
}

/** Phase-2: 최근 ISO timestamp 를 "오늘 / N일 전 / 절대일자" 로 표시. */
function formatRelative(iso: string | null): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const day = 24 * 60 * 60 * 1000;
  const diffDays = Math.floor((Date.now() - t) / day);
  if (diffDays < 1) return "오늘";
  if (diffDays < 7) return `${diffDays}일 전`;
  return new Date(iso).toLocaleDateString("ko-KR");
}

function PipelineRow({
  row,
  action,
  isExpanded,
  onExpand,
  checked,
  onToggle,
  onRunConverter,
  onRunFailFix,
  onRunValidateFix,
  onShowDetail,
  converterRunning,
  failFixRunning,
  validateFixRunning,
  tenantCode,
  bsda,
  summary,
  featureV2 = false,
}: PipelineRowProps) {
  const bgColor =
    row.converterStatus === "failed"
      ? "error.50"
      : row.converterStatus === "no_file"
        ? "warning.50"
        : action === "done"
          ? "success.50"
          : undefined;

  return (
    <>
      <TableRow hover sx={{ bgcolor: bgColor }}>
        <TableCell padding="checkbox">
          <Checkbox size="small" checked={checked} onChange={onToggle} />
        </TableCell>
        <TableCell padding="none">
          <IconButton size="small" onClick={onExpand}>
            {isExpanded ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
            {row.promptId}
          </Typography>
        </TableCell>
        <TableCell>{statusChip(row.converterStatus)}</TableCell>
        <TableCell align="right">
          <Typography variant="caption">
            {row.recordCount != null ? row.recordCount.toLocaleString() : "—"}
          </Typography>
        </TableCell>
        {featureV2 && (
          <TableCell>
            {summary ? (
              <Tooltip title={summary.last_run_at ?? ""} arrow>
                <Typography variant="caption">
                  {formatRelative(summary.last_run_at)}
                  {summary.last_bsda && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                      ({summary.last_bsda})
                    </Typography>
                  )}
                </Typography>
              </Tooltip>
            ) : (
              <Typography variant="caption" color="text.secondary">—</Typography>
            )}
          </TableCell>
        )}
        {featureV2 && (
          <TableCell>
            {summary ? (
              summary.consecutive_failures === 0 ? (
                <Chip size="small" color="success" variant="outlined" label="정상" />
              ) : (
                <Chip
                  size="small"
                  color={summary.consecutive_failures >= 3 ? "error" : "warning"}
                  variant="outlined"
                  label={`${summary.consecutive_failures}회 연속`}
                />
              )
            ) : (
              <Typography variant="caption" color="text.secondary">—</Typography>
            )}
          </TableCell>
        )}
        <TableCell>{skillChip(row.failFix)}</TableCell>
        <TableCell>{skillChip(row.validateFix)}</TableCell>
        <TableCell>
          {row.fixLoop ? (
            <Tooltip title={fixLoopTooltip(row.fixLoop)} arrow>
              <Chip
                size="small"
                color={fixLoopColor(row.fixLoop.status)}
                variant="outlined"
                label={fixLoopLabel(row.fixLoop)}
              />
            </Tooltip>
          ) : (
            <Tooltip title="이 PROMPT는 아직 파이프라인이 돌지 않았음" arrow>
              <Typography variant="caption" color="text.secondary">—</Typography>
            </Tooltip>
          )}
        </TableCell>
        <TableCell align="right">
          <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
          {action === "run_converter" && (
            <Button
              size="small"
              variant="contained"
              startIcon={<PlayArrowIcon />}
              onClick={onRunConverter}
              disabled={converterRunning}
            >
              {row.converterStatus === "no_file" ? "재실행" : "컨버터"}
            </Button>
          )}
          {action === "run_fail_fix" && (
            <Button
              size="small"
              variant="contained"
              color="warning"
              startIcon={<PlayArrowIcon />}
              onClick={onRunFailFix}
              disabled={failFixRunning}
            >
              fail_fix
            </Button>
          )}
          {action === "run_validate_fix" && (
            <Button
              size="small"
              variant="contained"
              color="info"
              startIcon={<PlayArrowIcon />}
              onClick={onRunValidateFix}
              disabled={validateFixRunning}
            >
              validate_fix
            </Button>
          )}
          {(action === "fail_fix_running" || action === "validate_fix_running") && (
            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
              <CircularProgress size={12} />
              <Typography variant="caption">
                {action === "fail_fix_running" ? "fail_fix" : "validate_fix"}
              </Typography>
            </Stack>
          )}
          {action === "done" && (
            <Chip size="small" color="success" label="완료" variant="outlined" />
          )}
          {onShowDetail && (
            <Tooltip title="세션 상세 (fix_attempts + validation_report + GCS 링크)" arrow>
              <IconButton size="small" onClick={onShowDetail} aria-label="세션 상세">
                <OpenInFullIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          </Stack>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={featureV2 ? 11 : 9} sx={{ py: 0, borderBottom: isExpanded ? 1 : 0 }}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, bgcolor: "action.hover" }}>
              <Stack spacing={1}>
                {featureV2 && tenantCode && bsda && isExpanded && (
                  <ColumnMetadataBlock
                    tenantCode={tenantCode}
                    promptId={row.promptId}
                    bsda={bsda}
                  />
                )}
                {tenantCode && bsda && isExpanded && (
                  <ValidationDetails
                    tenantCode={tenantCode}
                    bsda={bsda}
                    promptId={row.promptId}
                  />
                )}
                {row.converterEntry && (
                  <ExpandedField label="컨버터 시작" value={formatTime(row.converterEntry.started_at)} />
                )}
                {row.converterEntry?.completed_at && (
                  <ExpandedField
                    label="컨버터 종료"
                    value={formatTime(row.converterEntry.completed_at)}
                  />
                )}
                {row.converterEntry?.output_path && (
                  <ExpandedField
                    label="Output"
                    value={row.converterEntry.output_path}
                    mono
                  />
                )}
                {row.error && (
                  <Alert severity="error" sx={{ mt: 0.5 }}>
                    <Typography
                      variant="caption"
                      sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}
                    >
                      {row.error}
                    </Typography>
                  </Alert>
                )}
                {row.failFix?.resultSummary && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      fail_fix 결과
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}
                    >
                      {row.failFix.resultSummary}
                    </Typography>
                  </Box>
                )}
                {row.validateFix?.resultSummary && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      validate_fix 결과
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}
                    >
                      {row.validateFix.resultSummary}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function ExpandedField({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <Stack direction="row" spacing={1}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 100 }}>
        {label}
      </Typography>
      <Typography
        variant="caption"
        sx={{ fontFamily: mono ? "monospace" : undefined, wordBreak: "break-all" }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

/** Phase-2: 변환 결과의 output_columns / record_count 를 펼침 영역에 표시. */
function ColumnMetadataBlock({
  tenantCode,
  promptId,
  bsda,
}: {
  tenantCode: string;
  promptId: string;
  bsda: string;
}) {
  const [meta, setMeta] = useState<ConverterMetadataResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchConverterMetadata(tenantCode, promptId, bsda)
      .then((resp) => {
        if (!cancelled) setMeta(resp);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "load failed");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantCode, promptId, bsda]);

  if (loading) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <CircularProgress size={14} />
        <Typography variant="caption" color="text.secondary">컬럼 메타 로딩…</Typography>
      </Stack>
    );
  }
  if (error) {
    return <Typography variant="caption" color="text.secondary">컬럼 메타 로드 실패: {error}</Typography>;
  }
  if (!meta || !meta.available) {
    return <Typography variant="caption" color="text.secondary">이번 BSDA 에 변환 메타가 없습니다.</Typography>;
  }
  return (
    <Box>
      <Typography variant="caption" fontWeight={600}>
        변환 컬럼 ({meta.output_columns.length}개)
        {meta.record_count != null && (
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            · 건수 {meta.record_count.toLocaleString()}
          </Typography>
        )}
      </Typography>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
        {meta.output_columns.length === 0 ? (
          <Typography variant="caption" color="text.secondary">컬럼 정보 없음</Typography>
        ) : (
          meta.output_columns.map((c) => (
            <Chip key={c} size="small" variant="outlined" label={c} sx={{ fontFamily: "monospace" }} />
          ))
        )}
      </Stack>
    </Box>
  );
}

/** Validation (ralph_validate.py) 상세 — 컬럼 일치 / 중복 / 날짜 완전성 / 건수 체크. */
function ValidationDetails({
  tenantCode,
  bsda,
  promptId,
}: {
  tenantCode: string;
  bsda: string;
  promptId: string;
}) {
  const [report, setReport] = useState<ValidationReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchValidationReport(tenantCode, { bsda, prompt: promptId })
      .then((r) => {
        if (!cancelled) setReport(r);
      })
      .catch((e) => {
        if (!cancelled) {
          setReport(null);
          setError(
            e?.status === 404
              ? "아직 validate_fix 가 실행되지 않음"
              : (e?.message ?? "조회 실패"),
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantCode, bsda, promptId]);

  if (loading) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <CircularProgress size={12} />
        <Typography variant="caption" color="text.secondary">
          검증 결과 불러오는 중…
        </Typography>
      </Stack>
    );
  }

  if (error) {
    return (
      <Typography variant="caption" color="text.secondary">
        📋 {error}
      </Typography>
    );
  }

  if (!report) return null;

  const overallPass = report.overall_status === "PASS";

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        bgcolor: overallPass ? "success.50" : "error.50",
        border: 1,
        borderColor: overallPass ? "success.light" : "error.light",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" mb={0.8}>
        <Typography variant="caption" fontWeight={600}>
          검증 결과 ({report.validation_method ?? "ralph_validate"}):
        </Typography>
        <Chip
          size="small"
          color={overallPass ? "success" : "error"}
          label={overallPass ? "전체 통과" : "실패"}
        />
        {report.overall_reason && (
          <Typography variant="caption" color="text.secondary">
            {report.overall_reason}
          </Typography>
        )}
      </Stack>

      <Stack spacing={0.5}>
        {report.checks.map((c, i) => (
          <ValidationCheckRow key={`${c.check_name}-${i}`} check={c} />
        ))}
      </Stack>
    </Box>
  );
}

function ValidationCheckRow({ check }: { check: ValidationReport["checks"][0] }) {
  const pass = check.status === "PASS";
  const nameMap: Record<string, string> = {
    column_check: "컬럼 일치",
    no_duplicate: "중복 없음",
    date_completeness: "날짜 완전성",
    date_range: "날짜 범위",
    record_count: "레코드 건수",
    empty_column: "빈 컬럼 탐지",
  };
  const displayName = nameMap[check.check_name] ?? check.check_name;

  const formatVal = (v: unknown): string => {
    if (v == null) return "";
    if (Array.isArray(v)) return `[${v.join(", ")}]`;
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="baseline">
        <Typography variant="caption" sx={{ minWidth: 110, fontFamily: "monospace" }}>
          {pass ? "✓" : "✗"} {displayName}
        </Typography>
        <Typography
          variant="caption"
          color={pass ? "success.main" : "error.main"}
          fontWeight={600}
        >
          {check.status}
        </Typography>
        {check.detail && (
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            {check.detail}
          </Typography>
        )}
      </Stack>
      {!pass && check.expected != null && (
        <Stack direction="row" spacing={1} sx={{ pl: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>
            기대:
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
            {formatVal(check.expected)}
          </Typography>
        </Stack>
      )}
      {!pass && check.actual != null && (
        <Stack direction="row" spacing={1} sx={{ pl: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>
            실제:
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
            {formatVal(check.actual)}
          </Typography>
        </Stack>
      )}
    </Box>
  );
}
